import api from '@/lib/api';

/**
 * Kalender — persönliche Termine + gemeinsame Planung mit Desk-Kollegen.
 * Sichtbar sind nur eigene Termine und solche, zu denen man eingeladen ist.
 */

export type EventColor = 'blue' | 'green' | 'violet' | 'amber' | 'red' | 'slate';

export interface EventAttendee {
  user_id: string;
  status: 'invited' | 'accepted' | 'declined';
  name: string;
}

export interface CalendarEvent {
  id: string;
  organization_id: string;
  created_by: string;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  color: EventColor | null;
  creator_first_name?: string | null;
  creator_last_name?: string | null;
  is_mine?: boolean;
  my_status?: 'invited' | 'accepted' | 'declined' | null;
  attendees: EventAttendee[];
}

export interface EventInput {
  title: string;
  description?: string;
  starts_at: string;
  ends_at: string;
  all_day?: boolean;
  color?: EventColor;
  attendee_ids?: string[];
  organization_id?: string;
}

const calendarService = {
  async list(from: Date, to: Date): Promise<CalendarEvent[]> {
    const { data } = await api.get('/calendar/events', {
      params: { from: from.toISOString(), to: to.toISOString() },
    });
    return data.data;
  },

  async colleagues(organizationId?: string) {
    const { data } = await api.get('/calendar/colleagues', { params: { organizationId } });
    return data.data as { id: string; first_name: string | null; last_name: string | null; email: string; role: string }[];
  },

  async create(input: EventInput): Promise<CalendarEvent> {
    const { data } = await api.post('/calendar/events', input);
    return data.data;
  },

  async update(id: string, input: Partial<EventInput>): Promise<CalendarEvent> {
    const { data } = await api.put(`/calendar/events/${id}`, input);
    return data.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/calendar/events/${id}`);
  },

  async respond(id: string, status: 'accepted' | 'declined'): Promise<void> {
    await api.post(`/calendar/events/${id}/respond`, { status });
  },
};

export default calendarService;
